import * as i0 from '@angular/core';
import { signal, effect, inject, ElementRef, NgZone, DestroyRef, output, Directive } from '@angular/core';
import { safeTakeUntilDestroyed } from 'ng-primitives/utils';
import { Observable } from 'rxjs';

/**
 * A simple helper function to create a resize observer as an RxJS Observable.
 * @param element The element to observe for resize events.
 * @returns The resize event as an Observable.
 */
function fromResizeEvent(element) {
  return new Observable(observer => {
    // ResizeObserver may not be available in all environments, so check for its existence
    if (typeof window === 'undefined' || typeof window.ResizeObserver === 'undefined') {
      // ResizeObserver is not available (SSR or unsupported browser)
      // Complete the observable without emitting any values
      observer.complete();
      return;
    }
    const resizeObserver = new ResizeObserver(entries => {
      // if there are no entries, ignore the event
      if (!entries.length) {
        return;
      }
      // otherwise, take the first entry and emit the dimensions
      const entry = entries[0];
      if ('borderBoxSize' in entry) {
        const borderSizeEntry = entry['borderBoxSize'];
        // this may be different across browsers so normalize it
        const borderSize = Array.isArray(borderSizeEntry) ? borderSizeEntry[0] : borderSizeEntry;
        observer.next({
          width: borderSize['inlineSize'],
          height: borderSize['blockSize']
        });
      } else {
        // fallback for browsers that don't support borderBoxSize
        observer.next({
          width: element.offsetWidth,
          height: element.offsetHeight
        });
      }
    });
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  });
}
/**
 * A utility function to observe any element for resize events and return the dimensions as a signal.
 */
function observeResize(elementFn) {
  const dimensions = signal({
    width: 0,
    height: 0
  });
  // store the subscription to the resize event
  let subscription = null;
  effect(() => {
    const targetElement = elementFn();
    if (!targetElement) {
      return;
    }
    // if we already have a subscription, unsubscribe from it
    subscription?.unsubscribe();
    // create a new subscription to the resize event
    subscription = fromResizeEvent(targetElement).subscribe(event => dimensions.set({
      width: event.width,
      height: event.height
    }));
  });
  return dimensions;
}

/**
 * Apply the `ngpResize` directive to an element to listen for resize events.
 */
let NgpResize = /*#__PURE__*/(() => {
  class NgpResize {
    constructor() {
      /**
       * Access the element.
       */
      this.element = inject(ElementRef);
      /**
       * Access zone.js
       */
      this.ngZone = inject(NgZone);
      /**
       * Access the destroy ref
       */
      this.destroyRef = inject(DestroyRef);
      /**
       * Emits when the element is resized.
       */
      this.didResize = output({
        alias: 'ngpResize'
      });
    }
    ngOnInit() {
      // oberve the element for resize events
      fromResizeEvent(this.element.nativeElement).pipe(safeTakeUntilDestroyed(this.destroyRef)).subscribe(event => this.ngZone.run(() => this.didResize.emit(event)));
    }
    static {
      this.ɵfac = function NgpResize_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpResize)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpResize,
        selectors: [["", "ngpResize", ""]],
        outputs: {
          didResize: "ngpResize"
        }
      });
    }
  }
  return NgpResize;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgpResize, fromResizeEvent, observeResize };
